import axios, { AxiosError } from "axios";
import { IFavoritesDTO } from "../Models/DTOs/IFavoritesDTO";

const BACKEND_URL = process.env.REACT_APP_WEB_API_URL;

export type FavoritesTypeFilter = "all" | "series" | "video";
export type FavoritesOrder = "asc" | "desc";

export interface IListFavoritesParams {
    take?: number;
    skip?: number;
    publishedOnly?: boolean;
    type?: FavoritesTypeFilter;
    order?: FavoritesOrder;
}

/**
 * GET /v2/users/{userId}/favorites?take=&skip=&publishedOnly=&type=&order=
 * Returns: { items: any[]; count: number } (shape depends on backend DTOs)
 */
export async function ListFavorites(
    aspNetUserId: string,
    abortController: AbortController,
    params?: IListFavoritesParams,
    accessToken?: string
): Promise<IFavoritesDTO | AxiosError> {
    const url = `${BACKEND_URL}v2/users/${encodeURIComponent(aspNetUserId)}/favorites`;

    const query: Record<string, string | number> = {};
    if (params?.take !== undefined) query.take = params.take;
    if (params?.skip !== undefined) query.skip = params.skip;
    if (params?.publishedOnly !== undefined) query.publishedOnly = params.publishedOnly ? "true" : "false";
    if (params?.type) query.type = params.type;
    if (params?.order) query.order = params.order;

    try {
        const res = await axios.get(url, {
            signal: abortController.signal,
            params: query,
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        });
        return res.data as IFavoritesDTO;
    } catch (err) {
        const error = err as AxiosError;
        console.error("ListFavorites error", error);
        return error;
    }
}

/**
 * POST /v1/users/{userId}/favorites/series/{seriesId}
 */
export async function AddFavoriteSeries(
    aspNetUserId: string,
    seriesId: number,
    access_token: string,
    abortController: AbortController
): Promise<true | AxiosError> {
    const url = `${BACKEND_URL}v1/users/${encodeURIComponent(aspNetUserId)}/favorites/series/${encodeURIComponent(
        String(seriesId)
    )}`;

    try {
        await axios.post(
            url,
            null, // no body needed
            {
                signal: abortController.signal,
                headers: { Authorization: "Bearer " + access_token }
            }
        );
        return true;
    } catch (err) {
        const error = err as AxiosError;
        console.error("AddFavoriteSeries error", error);
        return error;
    }
}

/**
 * DELETE /v1/users/{userId}/favorites/series/{seriesId}
 */
export async function RemoveFavoriteSeries(
    aspNetUserId: string,
    seriesId: number,
    access_token: string,
    abortController: AbortController
): Promise<true | AxiosError> {
    const url = `${BACKEND_URL}v1/users/${encodeURIComponent(aspNetUserId)}/favorites/series/${encodeURIComponent(
        String(seriesId)
    )}`;

    try {
        await axios.delete(url, {
            signal: abortController.signal,
            headers: { Authorization: "Bearer " + access_token }
        });
        return true;
    } catch (err) {
        const error = err as AxiosError;
        console.error("RemoveFavoriteSeries error", error);
        return error;
    }
}

/**
 * POST /v1/users/{userId}/favorites/videos/{videoId}
 */
export async function AddFavoriteVideo(
    aspNetUserId: string,
    videoId: number,
    access_token: string,
    abortController: AbortController
): Promise<true | AxiosError> {
    const url = `${BACKEND_URL}v1/users/${encodeURIComponent(aspNetUserId)}/favorites/videos/${encodeURIComponent(
        String(videoId)
    )}`;

    try {
        await axios.post(
            url,
            null, // no body needed
            {
                signal: abortController.signal,
                headers: { Authorization: "Bearer " + access_token }
            }
        );
        return true;
    } catch (err) {
        const error = err as AxiosError;
        console.error("AddFavoriteVideo error", error);
        return error;
    }
}

/**
 * DELETE /v1/users/{userId}/favorites/videos/{videoId}
 */
export async function RemoveFavoriteVideo(
    aspNetUserId: string,
    videoId: number,
    access_token: string,
    abortController: AbortController
): Promise<true | AxiosError> {
    const url = `${BACKEND_URL}v1/users/${encodeURIComponent(aspNetUserId)}/favorites/videos/${encodeURIComponent(
        String(videoId)
    )}`;

    try {
        await axios.delete(url, {
            signal: abortController.signal,
            headers: { Authorization: "Bearer " + access_token }
        });
        return true;
    } catch (err) {
        const error = err as AxiosError;
        console.error("RemoveFavoriteVideo error", error);
        return error;
    }
}
